import { HANDLE_ERROR_LOCALLY_HEADER, showGlobalError, withLocalErrorHandling } from "@shared";
import i18next from "i18next";

import { ErrorContext, Middleware, ProblemDetails, ResponseContext } from "../api-client";

// The header constant and the init override that sets it live in @shared, so shared client-file code
// can opt out of the global toast too; re-exported here for this app's imports.
export { HANDLE_ERROR_LOCALLY_HEADER, withLocalErrorHandling };

async function tryParseBody(response: Response): Promise<ProblemDetails | null> {
  try {
    return (await response.clone().json()) as ProblemDetails;
  } catch {
    return null;
  }
}

function getFallbackMessage(status: number): string {
  if (status === 409) return i18next.t("common.conflictError");
  if (status === 422) return i18next.t("common.validationError");
  if (status === 403) return i18next.t("common.forbiddenError");
  if (status === 404) return i18next.t("common.notFoundError");
  if (status >= 500) return i18next.t("common.serverError");
  return i18next.t("common.requestError");
}

function isHandledLocally(init: RequestInit): boolean {
  const headers = init.headers;
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has(HANDLE_ERROR_LOCALLY_HEADER);
  if (Array.isArray(headers)) return headers.some(([key]) => key === HANDLE_ERROR_LOCALLY_HEADER);
  return HANDLE_ERROR_LOCALLY_HEADER in (headers as Record<string, string>);
}

export const errorMiddleware: Middleware = {
  async post(context: ResponseContext) {
    const { status } = context.response;

    if (status === 401) {
      window.location.href = "/login";
      return context.response;
    }

    if (status >= 400 && !isHandledLocally(context.init)) {
      const body = await tryParseBody(context.response);
      const message = body?.detail ?? body?.title ?? getFallbackMessage(status);
      showGlobalError(message, body?.activityTraceId ?? undefined);
    }

    return context.response;
  },

  async onError(context: ErrorContext) {
    console.error("API request failed:", context.error);
    showGlobalError(i18next.t("common.networkError"));
    return undefined;
  },
};
